/**
 * Mock user data for development and demonstration purposes
 * This file contains sample user profile data used in the application
 */

export interface UserProfile {
  id: string;
  username: string;
  name: string;
  title: string;
  location: string;
  email: string;
  phone: string;
  website: string;
  github: string;
  linkedin: string;
  avatar: string;
  coverImage: string;
  about: string;
  experience: Experience[];
  education: Education[];
  skills: Skill[];
  portfolio: PortfolioItem[];
  recommendations: Recommendation[];
  certifications: Certification[];
  openSourceContributions: OpenSourceContribution[];
  resume: ResumeFile;
  portfolioFiles: PortfolioFile[];
}

export interface Experience {
  id: number;
  title: string;
  company: string;
  companyLogo: string;
  location: string;
  startDate: string;
  endDate: string | null;
  description: string;
  achievements: string[];
}

export interface Education {
  id: number;
  degree: string;
  school: string;
  schoolLogo: string;
  field: string;
  startDate: string;
  endDate: string;
  gpa: string;
  description: string;
}

export interface Skill {
  name: string;
  level: "초급" | "중급" | "고급" | "전문가";
  category: "Frontend" | "Backend" | "Cloud" | "DevOps" | "Tools" | "Design";
}

export interface PortfolioItem {
  id: number;
  title: string;
  description: string;
  image: string;
  techStack: string[];
  liveUrl: string;
  githubUrl: string;
}

export interface Recommendation {
  id: number;
  recommenderName: string;
  recommenderTitle: string;
  recommenderAvatar: string;
  company: string;
  message: string;
  receivedAt: string;
}

export interface Certification {
  id: number;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate: string | null;
  credentialId: string;
  verificationUrl: string;
}

export interface OpenSourceContribution {
  id: number;
  projectName: string;
  description: string;
  repository: string;
  contributions: number;
  stars: number;
  language: string;
  lastContribution: string;
  role: "Contributor" | "Maintainer" | "Owner";
}

export interface ResumeFile {
  name: string;
  url: string;
  uploadedAt: string;
  size: number;
}

export interface PortfolioFile {
  id: number;
  name: string;
  url: string;
  uploadedAt: string;
  size: number;
  description: string;
}

/**
 * Mock user profile data
 * This data represents a sample user profile for demonstration purposes
 */
export const mockUserData: UserProfile = {
  id: "1",
  username: "kimdev",
  name: "김개발",
  title: "Senior Frontend Developer",
  location: "서울, 대한민국",
  email: "kim.dev@example.com",
  phone: "+82 10-1234-5678",
  website: "https://kimdev.portfolio.com",
  github: "https://github.com/kimdev",
  linkedin: "https://linkedin.com/in/kimdev",
  avatar:
    "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
  coverImage:
    "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=200&fit=crop",
  about:
    "5년 이상의 프론트엔드 개발 경험을 가진 개발자입니다. React, TypeScript, Next.js를 주로 사용하며, 사용자 경험을 중시하는 개발을 추구합니다. 팀워크를 중요하게 생각하며, 지속적인 학습과 성장을 통해 더 나은 개발자가 되고자 노력하고 있습니다.",
  experience: [
    {
      id: 1,
      title: "Senior Frontend Developer",
      company: "테커 테크",
      companyLogo:
        "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop",
      location: "서울, 대한민국",
      startDate: "2022-03",
      endDate: null,
      description:
        "React 기반 웹 애플리케이션 개발 및 팀 리딩을 담당했습니다. TypeScript 도입을 주도하고, 성능 최적화를 통해 페이지 로딩 속도를 40% 개선했습니다.",
      achievements: [
        "팀 내 TypeScript 도입 주도",
        "페이지 로딩 속도 40% 개선",
        "신입 개발자 멘토링",
        "컴포넌트 라이브러리 구축",
      ],
    },
    {
      id: 2,
      title: "Frontend Developer",
      company: "스타트업 코리아",
      companyLogo:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?w=60&h=60&fit=crop",
      location: "서울, 대한민국",
      startDate: "2020-06",
      endDate: "2022-02",
      description:
        "React와 Node.js를 사용한 풀스택 개발을 담당했습니다. 사용자 인터페이스 설계부터 백엔드 API 연동까지 전 과정을 담당했습니다.",
      achievements: [
        "React 기반 SPA 개발",
        "RESTful API 설계 및 구현",
        "반응형 웹 디자인 구현",
        "사용자 피드백 기반 UI/UX 개선",
      ],
    },
  ],
  education: [
    {
      id: 1,
      degree: "컴퓨터공학 학사",
      school: "서울대학교",
      schoolLogo:
        "https://images.unsplash.com/photo-1562774053-701939374585?w=60&h=60&fit=crop",
      field: "컴퓨터공학과",
      startDate: "2016-03",
      endDate: "2020-02",
      gpa: "3.8/4.0",
      description:
        "컴퓨터공학 전공으로 소프트웨어 개발의 기초를 탄탄히 다졌습니다. 데이터구조, 알고리즘, 소프트웨어공학 등의 과목을 우수한 성적으로 이수했습니다.",
    },
  ],
  skills: [
    { name: "React", level: "전문가", category: "Frontend" },
    { name: "TypeScript", level: "고급", category: "Frontend" },
    { name: "Next.js", level: "고급", category: "Frontend" },
    { name: "JavaScript", level: "전문가", category: "Frontend" },
    { name: "HTML/CSS", level: "전문가", category: "Frontend" },
    { name: "Node.js", level: "중급", category: "Backend" },
    { name: "Express", level: "중급", category: "Backend" },
    { name: "MongoDB", level: "중급", category: "Backend" },
    { name: "PostgreSQL", level: "중급", category: "Backend" },
    { name: "AWS", level: "중급", category: "Cloud" },
    { name: "Docker", level: "중급", category: "DevOps" },
    { name: "Git", level: "고급", category: "Tools" },
    { name: "Figma", level: "중급", category: "Design" },
  ],
  portfolio: [
    {
      id: 1,
      title: "E-commerce Platform",
      description:
        "React와 Node.js로 개발한 온라인 쇼핑몰 플랫폼입니다. 결제 시스템, 재고 관리, 사용자 인증 등의 기능을 포함합니다.",
      image:
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=250&fit=crop",
      techStack: ["React", "Node.js", "MongoDB", "Stripe"],
      liveUrl: "https://ecommerce-demo.com",
      githubUrl: "https://github.com/kimdev/ecommerce",
    },
    {
      id: 2,
      title: "Task Management App",
      description:
        "팀 협업을 위한 태스크 관리 애플리케이션입니다. 실시간 업데이트, 파일 공유, 댓글 시스템 등의 기능을 제공합니다.",
      image:
        "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=250&fit=crop",
      techStack: ["React", "TypeScript", "Socket.io", "PostgreSQL"],
      liveUrl: "https://taskmanager-demo.com",
      githubUrl: "https://github.com/kimdev/taskmanager",
    },
  ],
  recommendations: [
    {
      id: 1,
      recommenderName: "박팀장",
      recommenderTitle: "프론트엔드 팀장",
      recommenderAvatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face",
      company: "테커 테크",
      message:
        "김개발님은 뛰어난 기술력과 팀워크를 겸비한 개발자입니다. 복잡한 문제를 체계적으로 접근하고, 팀원들과의 소통도 원활합니다. 특히 TypeScript 도입을 주도하여 코드 품질을 크게 향상시켰습니다.",
      receivedAt: "2023-10-15",
    },
    {
      id: 2,
      recommenderName: "이동료",
      recommenderTitle: "백엔드 개발자",
      recommenderAvatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face",
      company: "테커 테크",
      message:
        "김개발님과 함께 일하면서 많은 것을 배웠습니다. 프론트엔드와 백엔드 간의 원활한 소통을 통해 프로젝트가 성공적으로 완료될 수 있었습니다. 항상 사용자 관점에서 생각하는 개발자입니다.",
      receivedAt: "2023-09-20",
    },
  ],
  certifications: [
    {
      id: 1,
      name: "AWS Certified Solutions Architect",
      issuer: "Amazon Web Services",
      issueDate: "2023-08-15",
      expiryDate: "2026-08-15",
      credentialId: "AWS-123456789",
      verificationUrl: "https://aws.amazon.com/verification",
    },
    {
      id: 2,
      name: "Google Cloud Professional Developer",
      issuer: "Google Cloud",
      issueDate: "2023-06-20",
      expiryDate: "2025-06-20",
      credentialId: "GCP-987654321",
      verificationUrl: "https://cloud.google.com/certification",
    },
    {
      id: 3,
      name: "React Developer Certification",
      issuer: "Meta",
      issueDate: "2023-04-10",
      expiryDate: null,
      credentialId: "REACT-456789123",
      verificationUrl: "https://react.dev/certification",
    },
  ],
  openSourceContributions: [
    {
      id: 1,
      projectName: "React",
      description: "React 라이브러리 핵심 기능 개선 및 버그 수정",
      repository: "facebook/react",
      contributions: 15,
      stars: 220000,
      language: "JavaScript",
      lastContribution: "2023-10-10",
      role: "Contributor",
    },
    {
      id: 2,
      projectName: "TypeScript",
      description: "TypeScript 컴파일러 성능 최적화",
      repository: "microsoft/TypeScript",
      contributions: 8,
      stars: 95000,
      language: "TypeScript",
      lastContribution: "2023-09-25",
      role: "Contributor",
    },
    {
      id: 3,
      projectName: "Next.js",
      description: "Next.js 프레임워크 문서화 및 예제 개선",
      repository: "vercel/next.js",
      contributions: 12,
      stars: 110000,
      language: "JavaScript",
      lastContribution: "2023-10-05",
      role: "Maintainer",
    },
    {
      id: 4,
      projectName: "My Custom Library",
      description: "개인적으로 개발한 React 컴포넌트 라이브러리",
      repository: "kimdev/react-components",
      contributions: 50,
      stars: 1200,
      language: "TypeScript",
      lastContribution: "2023-10-15",
      role: "Owner",
    },
  ],
  resume: {
    name: "김개발_이력서.pdf",
    url: "/resumes/kimdev_resume.pdf",
    uploadedAt: "2023-10-01",
    size: 1024000,
  },
  portfolioFiles: [
    {
      id: 1,
      name: "김개발_포트폴리오_2023.pdf",
      url: "/portfolios/kimdev_portfolio_2023.pdf",
      uploadedAt: "2023-10-01",
      size: 2048000,
      description: "주요 프로젝트 포트폴리오 모음",
    },
    {
      id: 2,
      name: "김개발_UIUX_작업물.pdf",
      url: "/portfolios/kimdev_uiux_works.pdf",
      uploadedAt: "2023-09-15",
      size: 1536000,
      description: "UI/UX 디자인 작업물 모음",
    },
  ],
};
